import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "@/components/ui/sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { ShareDialog, ShareButton } from "@/components/ShareDialog";
import { useFolders } from "@/hooks/useFolders";
import { useBulkSelection } from "@/hooks/useBulkSelection";
import { validateImageFile } from "@/lib/imageUpload";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { BrandLogoLibraryTab } from "@/components/BrandLogoLibraryTab";
import {
  Image as ImageIcon,
  Upload,
  Plus,
  Trash2,
  Edit2,
  Check,
  X,
  Loader2,
  FileImage,
  Building2,
  Share2,
  FolderOpen,
  Pencil,
  CheckCircle2,
  Search
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.05 }
  }
};

const itemVariants = {
  hidden: { opacity: 0, scale: 0.9 },
  visible: { opacity: 1, scale: 1 }
};

export const LogoLibraryPage = () => {
  const [logos, setLogos] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [editingLogo, setEditingLogo] = useState(null);
  const [editName, setEditName] = useState("");
  const [searchQuery, setSearchQuery] = useState("");

  // Bulk selection (shared hook)
  const { selectedIds, bulkMode, setBulkMode, setSelectedIds, toggleSelect, clearSelection } = useBulkSelection();

  // Share dialog state
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [shareTarget, setShareTarget] = useState(null);

  // Folder state + handlers (shared hook)
  const {
    folders,
    selectedFolderId,
    setSelectedFolderId,
    newFolderName,
    setNewFolderName,
    showNewFolder,
    setShowNewFolder,
    editingFolderId,
    setEditingFolderId,
    editFolderName,
    setEditFolderName,
    currentUser,
    handleCreateFolder,
    handleRenameFolder,
    handleDeleteFolder,
    handleToggleShareFolder,
    handleMoveToFolder,
    handleBulkMoveToFolder: bulkMoveToFolder,
    filterByFolder,
  } = useFolders({
    folderType: "logos",
    itemEndpoint: "/logos",
    setItems: setLogos,
    itemNounSingular: "Logo",
    itemNounPlural: "Logos",
  });

  // Upload form state
  const [uploadForm, setUploadForm] = useState({
    name: "",
    imageData: null,
    imagePreview: null
  });

  const fetchLogos = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await axios.get(`${API}/logos`);
      setLogos(response.data || []);
    } catch (err) {
      console.error("Error fetching logos:", err);
      // Only show error toast for actual errors, not empty results
      if (err.response?.status !== 403 && err.response?.status !== 404) {
        toast.error("Fehler beim Laden der Logos");
      }
      setLogos([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLogos();
  }, [fetchLogos]);

  // Bulk move selected logos to folder (wraps shared hook with selection cleanup)
  const handleBulkMoveToFolder = async (folderId) => {
    await bulkMoveToFolder(folderId, selectedIds, clearSelection);
  };

  // Filtered logos with search
  const filteredLogos = (() => {
    let result = filterByFolder(logos);
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(l => l.name?.toLowerCase().includes(q));
    }
    return result;
  })();

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!validateImageFile(file)) return;

    // Read file as base64
    const reader = new FileReader();
    reader.onload = (event) => {
      setUploadForm(prev => ({
        ...prev,
        imageData: event.target.result,
        imagePreview: event.target.result,
        name: prev.name || file.name.replace(/\.[^/.]+$/, "") // Use filename as default name
      }));
    };
    reader.readAsDataURL(file);
  };

  const handleUpload = async () => {
    if (!uploadForm.name.trim()) {
      toast.error("Bitte gib einen Namen ein");
      return;
    }
    if (!uploadForm.imageData) {
      toast.error("Bitte wähle ein Bild aus");
      return;
    }

    try {
      setIsUploading(true);
      await axios.post(`${API}/logos`, {
        name: uploadForm.name.trim(),
        image_data: uploadForm.imageData
      });
      
      toast.success(`Logo "${uploadForm.name}" erfolgreich hochgeladen!`);
      setShowUploadDialog(false);
      setUploadForm({ name: "", imageData: null, imagePreview: null });
      fetchLogos();
    } catch (err) {
      console.error("Error uploading logo:", err);
      toast.error("Fehler beim Hochladen des Logos");
    } finally {
      setIsUploading(false);
    }
  };

  const handleDelete = async (logoId, logoName) => {
    try {
      await axios.delete(`${API}/logos/${logoId}`);
      toast.success(`Logo "${logoName}" gelöscht`);
      fetchLogos();
    } catch (err) {
      console.error("Error deleting logo:", err);
      toast.error("Fehler beim Löschen");
    }
  };

  const handleUpdateName = async (logoId) => {
    if (!editName.trim()) {
      toast.error("Name darf nicht leer sein");
      return;
    }

    try {
      await axios.put(`${API}/logos/${logoId}`, { name: editName.trim() });
      toast.success("Logo-Name aktualisiert");
      setEditingLogo(null);
      setEditName("");
      fetchLogos();
    } catch (err) {
      console.error("Error updating logo:", err);
      toast.error("Fehler beim Aktualisieren");
    }
  };

  const startEditing = (logo) => {
    setEditingLogo(logo.id);
    setEditName(logo.name);
  };

  const cancelEditing = () => {
    setEditingLogo(null);
    setEditName("");
  };

  // Drag and drop handlers
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      const fakeEvent = { target: { files: [files[0]] } };
      handleFileSelect(fakeEvent);
    }
  };

  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <Building2 className="h-6 w-6 text-primary" />
            Logo-Bibliothek
          </h1>
          <p className="text-muted-foreground mt-1">
            Verwalte Kunden-Logos für deine Thumbnails
          </p>
        </div>
        
        <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
          <DialogTrigger asChild>
            <Button data-testid="upload-logo-button">
              <Plus className="h-4 w-4 mr-2" />
              Logo hochladen
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Neues Logo hochladen</DialogTitle>
              <DialogDescription>
                Lade ein Kunden-Logo hoch, das in Thumbnails verwendet werden kann.
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              {/* Drop Zone */}
              <div
                className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                  uploadForm.imagePreview 
                    ? 'border-primary bg-primary/5' 
                    : 'border-muted-foreground/25 hover:border-primary/50'
                }`}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
              >
                {uploadForm.imagePreview ? (
                  <div className="space-y-3">
                    <img 
                      src={uploadForm.imagePreview} 
                      alt="Preview" 
                      className="max-h-32 mx-auto object-contain rounded-md"
                    />
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => setUploadForm(prev => ({ ...prev, imageData: null, imagePreview: null }))}
                    >
                      <X className="h-4 w-4 mr-1" />
                      Entfernen
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <FileImage className="h-10 w-10 mx-auto text-muted-foreground/50" />
                    <div>
                      <p className="text-sm text-muted-foreground">
                        Bild hierher ziehen oder
                      </p>
                      <label className="cursor-pointer">
                        <span className="text-sm text-primary hover:underline">
                          Datei auswählen
                        </span>
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={handleFileSelect}
                          data-testid="logo-file-input"
                        />
                      </label>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      PNG, JPG, SVG (max. 5MB)
                    </p>
                  </div>
                )}
              </div>

              {/* Name Input */}
              <div className="space-y-2">
                <Label htmlFor="logo-name">Logo-Name</Label>
                <Input
                  id="logo-name"
                  placeholder="z.B. RAN Gruppe Logo"
                  value={uploadForm.name}
                  onChange={(e) => setUploadForm(prev => ({ ...prev, name: e.target.value }))}
                  data-testid="logo-name-input"
                />
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowUploadDialog(false)}>
                Abbrechen
              </Button>
              <Button 
                onClick={handleUpload} 
                disabled={isUploading || !uploadForm.imageData || !uploadForm.name.trim()}
                data-testid="confirm-upload-button"
              >
                {isUploading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Hochladen...
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4 mr-2" />
                    Hochladen
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Tabs defaultValue="own" className="w-full">
        <TabsList className="mb-4">
          <TabsTrigger value="own" data-testid="logo-tab-own">
            Eigene Logos
          </TabsTrigger>
          <TabsTrigger value="brand-library" data-testid="logo-tab-brand-library">
            Marken-Bibliothek
          </TabsTrigger>
        </TabsList>

        <TabsContent value="brand-library">
          <BrandLogoLibraryTab />
        </TabsContent>

        <TabsContent value="own" className="space-y-0">

      {/* Search + Bulk Actions Bar */}
      {!isLoading && logos.length > 0 && (
        <div className="flex items-center gap-3 mb-3">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input
              placeholder="Logo suchen..."
              className="w-full h-9 pl-9 pr-3 rounded-lg border bg-background text-sm focus:outline-none focus:ring-1 focus:ring-primary"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <Button
            variant={bulkMode ? "default" : "outline"}
            size="sm"
            onClick={() => { setBulkMode(!bulkMode); setSelectedIds(new Set()); }}
          >
            {bulkMode ? `${selectedIds.size} ausgewählt` : 'Auswählen'}
          </Button>
          {bulkMode && selectedIds.size > 0 && folders.length > 0 && (
            <select
              className="h-9 text-sm border rounded-lg px-2 bg-background cursor-pointer"
              value=""
              onChange={(e) => { if (e.target.value) handleBulkMoveToFolder(e.target.value); }}
            >
              <option value="">In Ordner verschieben...</option>
              <option value="">── Kein Ordner ──</option>
              {folders.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
            </select>
          )}
        </div>
      )}

      {/* Folder Tabs */}
      {!isLoading && logos.length > 0 && (
        <div className="flex items-center gap-2 mb-4 flex-wrap">
          <button onClick={() => setSelectedFolderId('all')} className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${selectedFolderId === 'all' ? 'bg-primary text-primary-foreground' : 'bg-muted hover:bg-muted/80 text-muted-foreground'}`}>
            Alle ({logos.length})
          </button>
          <button onClick={() => setSelectedFolderId('none')} className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${selectedFolderId === 'none' ? 'bg-primary text-primary-foreground' : 'bg-muted hover:bg-muted/80 text-muted-foreground'}`}>
            Ohne Ordner ({logos.filter(l => !l.folder_id).length})
          </button>
          {folders.map(folder => (
            <div key={folder.id} className="group relative flex items-center">
              {editingFolderId === folder.id ? (
                <input autoFocus className="px-3 py-1.5 rounded-full text-sm border focus:outline-none focus:ring-1 focus:ring-primary w-32" value={editFolderName} onChange={(e) => setEditFolderName(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleRenameFolder(folder.id); if (e.key === 'Escape') setEditingFolderId(null); }}
                  onBlur={() => handleRenameFolder(folder.id)} />
              ) : (
                <button onClick={() => setSelectedFolderId(folder.id)} className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors flex items-center gap-1.5 ${selectedFolderId === folder.id ? 'bg-primary text-primary-foreground' : 'bg-muted hover:bg-muted/80 text-muted-foreground'}`}>
                  <FolderOpen className="h-3.5 w-3.5" />
                  {folder.name} ({logos.filter(l => l.folder_id === folder.id).length})
                  {folder.shared_with_org && <Share2 className="h-3 w-3 opacity-60" />}
                </button>
              )}
              {(folder.owner_id === currentUser?.id || currentUser?.role === 'owner') && editingFolderId !== folder.id && (
                <div className="hidden group-hover:flex items-center gap-0.5 ml-1">
                  <button onClick={(e) => { e.stopPropagation(); setEditingFolderId(folder.id); setEditFolderName(folder.name); }} className="p-1 rounded hover:bg-muted" title="Umbenennen"><Pencil className="h-3 w-3 text-muted-foreground" /></button>
                  <button onClick={(e) => { e.stopPropagation(); handleToggleShareFolder(folder); }} className="p-1 rounded hover:bg-muted" title={folder.shared_with_org ? 'Freigabe aufheben' : 'Teilen'}><Share2 className={`h-3 w-3 ${folder.shared_with_org ? 'text-primary' : 'text-muted-foreground'}`} /></button>
                  <button onClick={(e) => { e.stopPropagation(); if (window.confirm(`Ordner "${folder.name}" löschen?`)) handleDeleteFolder(folder.id); }} className="p-1 rounded hover:bg-muted" title="Löschen"><Trash2 className="h-3 w-3 text-muted-foreground hover:text-destructive" /></button>
                </div>
              )}
            </div>
          ))}
          {showNewFolder ? (
            <input autoFocus placeholder="Ordnername..." className="px-3 py-1.5 rounded-full text-sm border focus:outline-none focus:ring-1 focus:ring-primary w-32" value={newFolderName} onChange={(e) => setNewFolderName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleCreateFolder(); if (e.key === 'Escape') { setShowNewFolder(false); setNewFolderName(''); } }}
              onBlur={() => { if (newFolderName.trim()) handleCreateFolder(); else { setShowNewFolder(false); setNewFolderName(''); } }} />
          ) : (
            <button onClick={() => setShowNewFolder(true)} className="px-3 py-1.5 rounded-full text-sm font-medium border border-dashed border-muted-foreground/40 hover:border-primary hover:text-primary transition-colors flex items-center gap-1 text-muted-foreground">
              <Plus className="h-3.5 w-3.5" /> Ordner
            </button>
          )}
        </div>
      )}

      {/* Loading State */}
      {isLoading && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
          {[...Array(6)].map((_, i) => (
            <Card key={i} className="overflow-hidden">
              <Skeleton className="aspect-square" />
              <CardContent className="p-3">
                <Skeleton className="h-4 w-3/4" />
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Empty State */}
      {!isLoading && logos.length === 0 && (
        <Card className="border-dashed">
          <CardContent className="py-12 text-center">
            <Building2 className="h-12 w-12 mx-auto text-muted-foreground/30 mb-4" />
            <h3 className="font-medium text-lg mb-1">Noch keine Logos</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Lade dein erstes Kunden-Logo hoch
            </p>
            <Button onClick={() => setShowUploadDialog(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Logo hochladen
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Logo Grid */}
      {!isLoading && filteredLogos.length > 0 && (
        <motion.div 
          className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <AnimatePresence>
            {filteredLogos.map((logo) => (
              <motion.div
                key={logo.id}
                variants={itemVariants}
                layout
              >
                <Card 
                  className={`overflow-hidden group hover:shadow-md transition-all cursor-pointer ${bulkMode && selectedIds.has(logo.id) ? 'ring-2 ring-primary' : ''}`}
                  data-testid={`logo-card-${logo.id}`}
                  onClick={() => bulkMode && toggleSelect(logo.id)}
                >
                  {/* Logo Image */}
                  <div className="aspect-square bg-muted/30 flex items-center justify-center p-4 relative">
                    <img
                      src={`${BACKEND_URL}${logo.image_url}?token=${localStorage.getItem('auth_token')}`}
                      alt={logo.name}
                      className="max-w-full max-h-full object-contain"
                      loading="lazy"
                    />
                    
                    {/* Bulk selection indicator */}
                    {bulkMode && (
                      <div className={`absolute top-2 left-2 w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors ${selectedIds.has(logo.id) ? 'bg-primary border-primary text-white' : 'bg-background/80 border-muted-foreground/50'}`}>
                        {selectedIds.has(logo.id) && <CheckCircle2 className="h-4 w-4" />}
                      </div>
                    )}

                    {/* Hover Actions - only show when not in bulk mode */}
                    {!bulkMode && (
                      <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button 
                              size="icon" 
                              variant="secondary"
                              onClick={() => {
                                setShareTarget(logo);
                                setShareDialogOpen(true);
                              }}
                              data-testid={`share-logo-${logo.id}`}
                            >
                              <Share2 className="h-4 w-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Teilen</TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                      
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button 
                              size="icon" 
                              variant="secondary"
                              onClick={() => startEditing(logo)}
                              data-testid={`edit-logo-${logo.id}`}
                            >
                              <Edit2 className="h-4 w-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Bearbeiten</TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                      
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button 
                            size="icon" 
                            variant="destructive"
                            data-testid={`delete-logo-${logo.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Logo löschen?</AlertDialogTitle>
                            <AlertDialogDescription>
                              Möchtest du das Logo "{logo.name}" wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => handleDelete(logo.id, logo.name)}
                              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            >
                              Löschen
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                    )}
                  </div>
                  
                  {/* Logo Name */}
                  <CardContent className="p-3">
                    {editingLogo === logo.id ? (
                      <div className="flex items-center gap-1">
                        <Input
                          value={editName}
                          onChange={(e) => setEditName(e.target.value)}
                          className="h-8 text-sm"
                          autoFocus
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleUpdateName(logo.id);
                            if (e.key === 'Escape') cancelEditing();
                          }}
                        />
                        <Button 
                          size="icon" 
                          variant="ghost" 
                          className="h-8 w-8 shrink-0"
                          onClick={() => handleUpdateName(logo.id)}
                        >
                          <Check className="h-4 w-4 text-green-500" />
                        </Button>
                        <Button 
                          size="icon" 
                          variant="ghost" 
                          className="h-8 w-8 shrink-0"
                          onClick={cancelEditing}
                        >
                          <X className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    ) : (
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-medium truncate flex-1" title={logo.name}>
                          {logo.name}
                        </p>
                        {logo.shared_with?.length > 0 && (
                          <Badge variant="secondary" className="ml-1 h-5 px-1.5 text-xs">
                            <Share2 className="h-3 w-3 mr-0.5" />
                            {logo.shared_with.length}
                          </Badge>
                        )}
                      </div>
                    )}
                    {/* Folder assignment */}
                    {folders.length > 0 && (
                      <select
                        className="w-full mt-1.5 h-7 text-xs border rounded px-1 bg-background text-muted-foreground cursor-pointer"
                        value={logo.folder_id || ''}
                        onChange={(e) => handleMoveToFolder(logo.id, e.target.value)}
                      >
                        <option value="">Kein Ordner</option>
                        {folders.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
                      </select>
                    )}
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>
      )}

      {/* Stats Footer */}
      {!isLoading && filteredLogos.length > 0 && (
        <div className="mt-6 flex items-center justify-between text-sm text-muted-foreground">
          <p>{filteredLogos.length} Logo{filteredLogos.length !== 1 ? 's' : ''}{selectedFolderId !== 'all' ? ` (${logos.length} gesamt)` : ' in der Bibliothek'}</p>
          <Badge variant="outline">
            <ImageIcon className="h-3 w-3 mr-1" />
            Bereit für Thumbnails
          </Badge>
        </div>
      )}

        </TabsContent>
      </Tabs>

      {/* Share Dialog */}
      <ShareDialog
        open={shareDialogOpen}
        onOpenChange={setShareDialogOpen}
        itemId={shareTarget?.id}
        itemType="logos"
        itemName={shareTarget?.name || ""}
        sharedWith={shareTarget?.shared_with || []}
        onShareUpdate={fetchLogos}
      />
    </div>
  );
};

export default LogoLibraryPage;
